/**
 * Shared helpers for the chatbot result cards. Every `chat-*-card` component
 * maps the same policy statuses to CSS classes and formats prices the same
 * way, so the logic lives here rather than being copy-pasted per card.
 */

/**
 * Map an OBT policy status to the CSS class the cards use for their policy
 * indicator. Unknown/missing statuses return an empty string.
 */
export function policyStatusClass(policyStatus: string | undefined | null): string {
  switch (policyStatus) {
    case 'IN_POLICY':
      return 'policy-ok';
    case 'ALLOW_WITH_REASON':
      return 'policy-warn';
    case 'UNAVAILABLE':
      return 'policy-block';
    default:
      return '';
  }
}

/**
 * Screen-reader-only label for a card's policy indicator, keyed off the same
 * CSS class `policyStatusClass` returns. The border-colour cue on its own
 * isn't perceivable to colour-blind or non-sighted users, so this gives every
 * policy state a text equivalent regardless of whether the card also has a
 * `policyMessages` string to show.
 */
export function policyStatusLabel(policyClass: string | undefined | null): string {
  switch (policyClass) {
    case 'policy-ok':
      return $localize`:@@chatPolicyStatusOk:In policy`;
    case 'policy-warn':
      return $localize`:@@chatPolicyStatusWarn:Requires approval`;
    case 'policy-block':
      return $localize`:@@chatPolicyStatusBlock:Unavailable`;
    default:
      return '';
  }
}

const currencyFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Format an amount as a GBP-locale currency string, caching one formatter per
 * currency code. Returns an empty string when the amount is missing.
 */
export function formatCardCurrency(amount: number | undefined | null, currency = 'GBP'): string {
  if (amount == null) {
    return '';
  }
  const code = currency || 'GBP';
  let formatter = currencyFormatters.get(code);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2
    });
    currencyFormatters.set(code, formatter);
  }
  return formatter.format(amount);
}
